import { ModuleWithProviders, NgModule, Optional, SkipSelf } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MAT_RIPPLE_GLOBAL_OPTIONS } from '@angular/material/core';
import { NbAuthJWTToken, NbAuthModule, NbDummyAuthStrategy } from '@nebular/auth';
import { NbSecurityModule, NbRoleProvider, NbAccessControl, NbAccessChecker } from '@nebular/security';
import { of as observableOf } from 'rxjs';
import { throwIfAlreadyLoaded } from './module-import-guard';
import {
  AnalyticsService,
  LayoutService,
  PlayerService,
  SeoService,
  StateService,
} from './utils';
import { RippleService } from './utils/ripple.service';
import { environment } from '../../environments/environment';
import { RoleProvider } from '../_helpers/role.provider';
import { CompaniesService } from '../_services/companies.service';
import { ExpofinAuthStrategy } from '../_helpers/expofin-auth.strategy';

const DATA_SERVICES = [
  {provide: MAT_RIPPLE_GLOBAL_OPTIONS, useExisting: RippleService},
];

export const NB_CORE_PROVIDERS = [
  ...DATA_SERVICES,
  ExpofinAuthStrategy,
  ...NbAuthModule.forRoot({

  // #####################
  // ### AUTH PROVIDER ###
  // #####################

    strategies: [

      // DOCS: https://akveo.github.io/nebular/docs/auth/nbpasswordauthstrategy#nbpasswordauthstrategy
      // Login via the Hasura `login` action; refresh/logout via auth_path (see ExpofinAuthStrategy).
      ExpofinAuthStrategy.setup({
        name: 'email',
        baseEndpoint: environment.api_path,
        token: {
          class: NbAuthJWTToken,
          key: 'accessToken',
        },
        // Cambio password
        resetPass: {
          endpoint: '/api/auth/change',
          method: 'post',
          requireValidToken: true,
          redirect: {
            success: '/login/',
            failure: '/change-password/',
          },
        },
        // Reset password
        requestPass: {
          endpoint: '/api/auth/reset',
          method: 'post',
          redirect: {
            success: '/login/',
            failure: '/',
          },
        },
        refreshToken: {
          endpoint: '/api/auth/refresh',
          method: 'post',
          requireValidToken: true,
          redirect: {
            success: null,
            failure: null,
          },
        },
        logout: {
          endpoint: '/api/auth/logout',
          method: 'post',
          redirect: {
            success: '/',
            failure: null,
          },
        },
        login: {
          endpoint: '/api/auth/login',
          method: 'post',
          redirect: {
            success: null,
            failure: null,
          },
        },
        register: {
          endpoint: '/api/auth/register',
          method: 'post',
          requireValidToken: false,
          redirect: {
            success: '/',
            failure: null,
          },
          defaultErrors: ["C'è stato un errore durante il processo di registrazione."],
          defaultMessages: ['Registrazione effettuata con successo. Attendi una EMail di conferma'],
        },
        // resetPass: {
        //   endpoint: '/auth/reset-pass',
        // },
      }),
    ],
    forms: {

      // ### IMPOSTAZIONI GUI FORM ###

      login: {
        redirectDelay: 500,
        rememberMe: true,
        showMessages: {
          success: true,
          error: true,
        },
      },
      logout: {
        redirectDelay: 0,
        showMessages: {
          success: true,
          error: true,
        },
      },
      register: {
        redirectDelay: 500,
        showMessages: {
          success: true,
          error: true,
        },
      },
      requestPass: {
        redirectDelay: 250,
        showMessages: {
          success: true,
          error: true,
        },
      },
      resetPass: {
        redirectDelay: 250,
        showMessages: {
          success: true,
          error: true,
        },
      },

      // ### IMPOSTAZIONI VALIDAZIONE VARIABILI ###

      validation: {

        companyName: {
          required: true,
          maxLength: 50,
        },
        companyFiscalCode: {
          required: true,
          //regexp: "^[A-Za-z]{6}[0-9]{2}[A-Za-z]{1}[0-9]{2}[A-Za-z]{1}[0-9]{3}[A-Za-z]{1}$",
          // minLength: 16,
          minLength: 11,
          maxLength: 16,
        },
        companyVat: {
          required: true,
          regexp: "^[0-9]{11}$",
          minLength: 11,
          maxLength: 11,
        },
        companyPhone: {
          required: true,
          minLength: 3,
          maxLength: 15,
        },
        companyWebsite: {
          required: false,
          minLength: 8,
          maxLength: 100,
          regexp: new RegExp(/^[(http(s)?):\/\/(www\.)?\w\-\/=#%&\.\?]{2,}\.[a-z]{2,}([\w\-\/=#%&\.\?]*)$/),
        },
        companyEmail: {
          required: true,
          regexp: ".+@.+..+",
        },
        userEmail:{
          required: true,
          regexp: ".+@.+..+",
        },
        companyPec: {
          required: true,
          regexp: ".+@.+..+",
        },
        companyAtecoCode: {
          required: true,
          regexp: "^(([0-9]{1,2})[.]?[0-9]{1,2}[.]?[0-9]{1,2}$)|^([0-9]{1,2}$|^[A-Z]{1}$)",
        },
        // Indirizzo Impresa
        companyStreet: {
          required: true,
        },
        companyHouseNumber: {
          required: true,
        },
        companyCity: {
          required: true,
          minLength: 1,
          maxLength: 50,
        },
        companyProvince: {
          required: true,
        },
        companyZip: {
          required: true,
          regexp: "^[0-9]{5}$",
          minLength: 5,
          maxLength: 5,
        },
        companyCountry: {
          required: true,
        },
        userPhone: {
          required: true
        },
        username: {
          required: true,
          minLength: 2,
          maxLength: 50,
        },
        userSurname: {
          required: true,
          minLength: 2,
          maxLength: 50,
        },
        userFiscalCode: {
          required: true,
          regexp: "^[A-Za-z]{6}[0-9]{2}[A-Za-z]{1}[0-9]{2}[A-Za-z]{1}[0-9]{3}[A-Za-z]{1}$",
          minLength: 16,
          maxLength: 16,
        },
        password: {
          required: true,
          minLength: 4,
          maxLength: 50,
        },
      },
    },
  }).providers??[],

  // #####################
  // ### ROLE PROVIDER ###
  // #####################

  NbSecurityModule.forRoot({
    accessControl: {
      // Non autenticato
      guest: {
        view: ['auth'],
      },
      // Collaboratore/Dipendente Azienda
      user: {
        parent: 'guest',
        view: ['home','changePsw'],
        create: [],
        edit: [],
        remove: [],
      },
      administrator: {
        parent: 'guest',
        view: ['home', 'changePsw', 'users', 'team_expofin', 'crm', 'crm_clients', 'metrics', 'devices','impianti','company', 'metric_groups', 'reports', 'app', 'ev_charger', 'risorse','cer', 'ev_dashboard', 'notifications', 'ev_history'],
        create: '*',
        edit: '*',
        remove: '*',
      },
      ev_manager: {
        parent: 'guest',
        view: ['changePsw', 'ev_charger', 'ev_dashboard', 'ev_history'],
        create: '*',
        edit: '*',
        remove: '*',
      },
      supervisor: {
        parent: 'guest',
        view: ['home', 'changePsw', 'users', 'team_expofin', 'crm', 'crm_clients', 'metrics', 'devices','impianti','company', 'metric_groups', 'reports', 'app', 'risorse', 'ev_dashboard', 'ev_charger', 'notifications','ev_history'],
        create: '*',
        edit: '*',
        remove: '*',
      },
      manager: {
        parent: 'guest',
        view: ['home', 'changePsw', 'users', 'crm', 'company', 'impianti','risorse', 'reports'],
        create: '*',
        edit: '*',
        remove: '*',
      },
      sales_director: {
        parent: 'guest',
        view: ['home', 'changePsw', 'users', 'team_expofin', 'crm', 'crm_clients', 'company', 'impianti', 'risorse', 'notifications'],
        create: '*',
        edit: '*',
        remove: '*',
      },
      sales_manager: {
        parent: 'guest',
        view: ['home', 'changePsw', 'users', 'team_expofin', 'crm', 'crm_clients', 'company', 'impianti', 'risorse'],
        create: ['team_expofin', 'crm'],
        edit: ['team_expofin', 'crm'],
        remove: [],
      },
      vendite: {
        parent: 'guest',
        view: ['home', 'changePsw', 'users', 'team_expofin', 'crm', 'crm_clients', 'company'],
        create: ['crm'],
        edit: '*',
        remove: [],
      },
      installer: {
        parent: 'guest',
        view: ['home', 'changePsw', 'users', 'crm', 'crm_clients', 'company', 'impianti', 'devices'],
        create: [],
        edit: [],
        remove: [],
      },
      technician: {
        parent: 'guest',
        view: ['home', 'changePsw', 'users', 'company', 'impianti','risorse', 'reports'],
        create: '*',
        edit: '*',
        remove: '*',
      },
      customer_companies: {
        parent: 'guest',
        view: ['home', 'changePsw', 'company', 'impianti','risorse', 'reports'],
        create: '*',
        edit: '*',
        remove: '*',
      },
      customer_private: {
        parent: 'guest',
        view: ['home', 'changePsw','impianti','risorse', 'reports'],
        create: '*',
        edit: '*',
        remove: '*',
      },
    },
  }).providers??[],

  {
    provide: NbRoleProvider, useClass: RoleProvider,
  },

  AnalyticsService,
  LayoutService,
  PlayerService,
  SeoService,
  StateService,
];

@NgModule({
  imports: [
    CommonModule,
  ],
  exports: [
    NbAuthModule,
  ],
  declarations: [],
})
export class CoreModule {
  constructor(@Optional() @SkipSelf() parentModule: CoreModule, private roleProvider: RoleProvider,
       private companiesService: CompaniesService) {

  }

  static forRoot(): ModuleWithProviders<CoreModule> {
    return {
      ngModule: CoreModule,
      providers: [
        ...NB_CORE_PROVIDERS,
      ],
    };
  }
}
